import React from "react";
import { Button, Container, Group, Text, Title, rem, createStyles } from "@mantine/core";

// Only retry-and-reload once within this window, rather than looping forever - if the chunk
// genuinely can't load (a bad deploy, a CDN outage) this still falls through to the normal
// fallback screen below instead of silently reloading over and over.
const CHUNK_LOAD_RETRY_KEY = "chunk-load-retry-at";
const CHUNK_LOAD_RETRY_COOLDOWN_MS = 10_000;

// Lazy-loaded route chunks are named/hashed per deploy. If a new build has shipped since this tab
// was first opened, navigating to a route whose chunk no longer exists at its old URL throws one
// of these errors (the exact name/message varies by browser/bundler) instead of loading - this is
// a very common "it only happens sometimes, and only on navigation" crash, unrelated to any bug in
// the page itself.
function isChunkLoadError(error) {
  const message = String(error?.message || "");
  return (
    error?.name === "ChunkLoadError" ||
    /loading chunk [\d]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    paddingTop: rem(80),
    paddingBottom: rem(80),
  },
  title: {
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
    textAlign: "center",
    fontWeight: 900,
    fontSize: rem(38),

    [theme.fn.smallerThan("sm")]: {
      fontSize: rem(32),
    },
  },
  description: {
    maxWidth: rem(500),
    margin: "auto",
    marginTop: theme.spacing.xl,
    marginBottom: `calc(${theme.spacing.xl} * 1.5)`,
  },
}));

// A plain function component so the styled fallback screen can still use Mantine's `createStyles`
// (this file only needs to render once the boundary below has already caught an error, not while
// rendering the app itself).
function ErrorFallback({ onReload, onGoHome }) {
  const { classes } = useStyles();
  return (
    <Container className={classes.root}>
      <Title className={classes.title}>Something went wrong.</Title>
      <Text color="dimmed" size="lg" align="center" className={classes.description}>
        This page hit an unexpected error and couldn't finish loading. Reloading usually fixes it -
        if it keeps happening, please let support know what you were doing right before this
        appeared.
      </Text>
      <Group position="center">
        <Button size="md" onClick={onReload}>
          Reload page
        </Button>
        <Button variant="subtle" size="md" onClick={onGoHome}>
          Take me back to home page
        </Button>
      </Group>
    </Container>
  );
}

// Error boundaries can only be implemented as class components (getDerivedStateFromError /
// componentDidCatch have no Hook equivalent as of React 18) - this is the one exception to the
// rest of the app being function components.
//
// Before this existed, ANY unhandled render error anywhere in the app - a missing field on a
// Firestore doc, a null reference, anything - unmounted the entire React tree with nothing in its
// place, i.e. a blank white page with no indication anything had gone wrong or how to recover.
// This catches that class of crash at the top level (wrapped around <App/> in index.js, so it
// still sits inside MantineProvider/ColorSchemeProvider and can render a normal styled screen)
// and offers a reload instead of a silent blank screen.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Unhandled render error caught by ErrorBoundary:", error, info?.componentStack);

    if (isChunkLoadError(error)) {
      const lastRetry = Number(window.sessionStorage.getItem(CHUNK_LOAD_RETRY_KEY) || 0);
      if (Date.now() - lastRetry > CHUNK_LOAD_RETRY_COOLDOWN_MS) {
        window.sessionStorage.setItem(CHUNK_LOAD_RETRY_KEY, String(Date.now()));
        // A full reload picks up the current build's chunk manifest - this is silent/automatic
        // rather than showing the generic error screen for what's really just a stale tab.
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          onReload={() => window.location.reload()}
          onGoHome={() => {
            this.setState({ hasError: false });
            window.location.assign("/");
          }}
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
